import { useState, useEffect, useRef, useCallback } from 'react';

const useAutoSlide = (items = [], interval = 3000, pauseOnHover = true) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  
  // Auto-advance to next slide
  const nextSlide = useCallback(() => {
    if (!items?.length) return;
    setCurrentIndex(prevIndex => (prevIndex + 1) % items?.length);
  }, [items?.length]);

  // Go to previous slide
  const prevSlide = useCallback(() => {
    if (!items?.length) return;
    setCurrentIndex(prevIndex => prevIndex === 0 ? items?.length - 1 : prevIndex - 1);
  }, [items?.length]);

  // Go to specific slide
  const goToSlide = useCallback((index) => {
    if (index >= 0 && index < items?.length) {
      setCurrentIndex(index);
    }
  }, [items?.length]);

  // Play auto-slide
  const play = useCallback(() => {
    setIsPlaying(true);
    setIsPaused(false);
  }, []);

  // Pause auto-slide
  const pause = useCallback(() => {
    setIsPaused(true);
  }, []);

  // Stop auto-slide completely
  const stop = useCallback(() => {
    setIsPlaying(false);
    setIsPaused(false);
  }, []);

  // Reset to first slide
  const reset = useCallback(() => {
    setCurrentIndex(0);
  }, []);

  // Mouse enter handler for pause on hover
  const handleMouseEnter = useCallback(() => {
    if (pauseOnHover && isPlaying) {
      setIsPaused(true);
    }
  }, [pauseOnHover, isPlaying]);

  // Mouse leave handler for resume on hover
  const handleMouseLeave = useCallback(() => {
    if (pauseOnHover && isPlaying) {
      setIsPaused(false);
    }
  }, [pauseOnHover, isPlaying]);

  // Clear all timers
  const clearTimers = useCallback(() => {
    if (intervalRef?.current) {
      clearInterval(intervalRef?.current);
      intervalRef.current = null;
    }
    if (timeoutRef?.current) {
      clearTimeout(timeoutRef?.current);
      timeoutRef.current = null;
    }
  }, []);

  // Effect to handle auto-slide logic
  useEffect(() => {
    // Don't auto-slide if not playing, paused, or no items
    if (!isPlaying || isPaused || !items?.length || items?.length <= 1) {
      clearTimers();
      return;
    }

    // Set up interval for auto-slide
    intervalRef.current = setInterval(() => {
      nextSlide();
    }, interval);

    return clearTimers;
  }, [isPlaying, isPaused, items?.length, interval, nextSlide, clearTimers]);

  // Effect to handle visibility change (pause when tab is not active)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsPaused(true);
      } else if (isPlaying) {
        // Resume after a small delay when tab becomes active
        timeoutRef.current = setTimeout(() => {
          setIsPaused(false);
        }, 500);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearTimers();
    };
  }, [isPlaying, clearTimers]);

  // Cleanup on unmount
  useEffect(() => {
    return clearTimers;
  }, [clearTimers]);

  // Current item
  const currentItem = items?.[currentIndex] || null;

  // Progress calculation (0-100)
  const progress = items?.length ? ((currentIndex + 1) / items?.length) * 100 : 0;

  return {
    // State
    currentIndex,
    currentItem,
    isPlaying,
    isPaused,
    progress,
    
    // Actions
    nextSlide,
    prevSlide,
    goToSlide,
    play,
    pause,
    stop,
    reset,
    
    // Event handlers for pause on hover
    handleMouseEnter,
    handleMouseLeave,
    
    // Utilities
    hasNext: currentIndex < (items?.length - 1),
    hasPrev: currentIndex > 0,
    totalSlides: items?.length || 0,
    
    // Status
    isFirst: currentIndex === 0,
    isLast: currentIndex === (items?.length - 1),
    
    // For debugging (removed process.env check for browser compatibility)
    ...(import.meta.env?.DEV && {
      debug: {
        intervalRef: intervalRef?.current,
        timeoutRef: timeoutRef?.current,
        itemsLength: items?.length,
        interval
      }
    })
  };
};

export default useAutoSlide;